import { io, type Socket } from 'socket.io-client';
import { z } from 'zod';
import {
  assessmentSchema,
  fileLinksSchema,
  jobErrorSchema,
  jobStatusSchema,
  placementSchema,
  qaSchema,
  sourceMetaSchema,
  type PackshotJob,
  type PackshotJobCard,
} from './packshots';

export const stageJobSchema = z.object({
  status: jobStatusSchema,
  stage: z.string().nullable(),
  productName: z.string().min(1).max(80).nullable(),
  attempts: z.number().int().nonnegative(),
  error: jobErrorSchema.nullable(),
  placement: placementSchema.nullable(),
  assessment: assessmentSchema.nullable(),
  qa: qaSchema.nullable(),
  files: fileLinksSchema,
  sourceMeta: sourceMetaSchema.nullable(),
  updatedAt: z.string().nullable(),
});

export const stageEventSchema = z.object({
  jobId: z.string().min(1),
  stage: z.enum(['ingest', 'assess', 'props', 'cleanup', 'cutout', 'compose', 'qa']),
  status: z.enum(['started', 'completed', 'skipped', 'failed']),
  fileUrl: z.string().min(1).nullable(),
  job: stageJobSchema,
});

export type StageEvent = z.infer<typeof stageEventSchema>;

export const watchAckSchema = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
});

export function applyStageToCard(card: PackshotJobCard, event: StageEvent): PackshotJobCard {
  if (event.jobId !== card.id) return card;
  const snap = event.job;
  return {
    ...card,
    status: snap.status,
    stage: snap.stage,
    productName: snap.productName,
    attempts: snap.attempts,
    error: snap.error,
    placement: snap.placement,
    files: snap.files,
    updatedAt: snap.updatedAt ?? card.updatedAt,
  };
}

export function applyStageToJob(job: PackshotJob, event: StageEvent): PackshotJob {
  if (event.jobId !== job.id) return job;
  const snap = event.job;
  return {
    ...job,
    ...applyStageToCard(job, event),
    assessment: snap.assessment ?? job.assessment,
    qa: snap.qa,
    sourceMeta: snap.sourceMeta,
  };
}

export function openPackshotSocket(): Socket {
  return io({
    path: '/socket.io',
    withCredentials: true,
    transports: ['websocket', 'polling'],
  });
}
